import React from 'react';
import type { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Check } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { colors, elevation } from '@skyslope/mache';
import cx from 'classnames';
import { handleKeyDown } from '../common/accessibility';
import { MD_MEDIA_QUERY } from '../common/constants';
import type { IBlock } from '../store/types';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    zIndex: 1,
    alignItems: 'center',
    height: '100%',
    width: '100%',
    '&:hover': {
      cursor: 'pointer',
      '& $checkbox:after': {
        opacity: 1,
      },
    },
  },
  checkbox: {
    height: 18,
    width: 18,
    backgroundColor: '#FFFFFF',
    border: `1px solid ${colors.grey[500]}`,
    borderRadius: '3px',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
    '&::after:not($disabled)': {
      boxShadow: elevation.normal[6],
      opacity: 0,
      transition: 'opacity 0.3s',
      content: '""',
      width: 'calc(100% + 2px)',
      height: 'calc(100% + 2px)',
      position: 'absolute' as const,
      zIndex: 0,
      top: 0,
      left: 0,
    },
  },
  disabled: {
    backgroundColor: colors.grey[500],
    cursor: 'default' as const,
  },
  checked: {
    backgroundColor: colors.blue[800],
  },
  check: {
    color: '#FFFFFF',
    height: 18,
    width: 18,
    position: 'absolute' as const,
    left: 0,
    top: 0,
  },
  label: {
    marginLeft: theme.spacing(2),
    userSelect: 'none' as const,
  },
}));
interface IProps {
  index: number;
  checked: boolean | string;
  onClick: (checked: boolean, blockId: string) => void;
  label?: string;
  disabled?: boolean;
  block: IBlock;
  required?: boolean;
  focused?: boolean;
  zoom: number;
}
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const Checkbox = (props: IProps) => {
  const classes = useStyles(props);
  const screenWidthIsLessThanOrEqualToMdBreakpoint = useMediaQuery(MD_MEDIA_QUERY);
  const borderRadius = screenWidthIsLessThanOrEqualToMdBreakpoint ? '2px' : '3px';
  let checkboxClasses = props.disabled && !props.checked ? cx(classes.checkbox, classes.disabled) : classes.checkbox;

  return (
    <div
      className={classes.wrapper}
      onClick={() => (props.disabled && !props.checked ? '' : props.onClick(!props.checked, props.block.blockId))}
      onKeyDown={(event) =>
        handleKeyDown(event, () =>
          props.disabled && !props.checked ? '' : props.onClick(!props.checked, props.block.blockId)
        )
      }
      role="checkbox"
      aria-checked={!!props.checked}
      aria-disabled={props.disabled}
      aria-required={props.required}
      tabIndex={props.disabled ? -1 : 0}
      data-spec={`checkbox-${props.block.blockId}`}
    >
      <div
        className={`${checkboxClasses} ${props.checked ? classes.checked : ''}`}
        style={{
          height: '100%',
          width: '100%',
          border: props.focused && props.required ? '1px solid #D20004' : '',
          borderRadius: borderRadius,
        }}
      >
        {props.checked ? <Check className={classes.check} style={{ height: '100%', width: '100%' }} /> : ''}
      </div>
      {props.label ? (
        <Typography variant="body1" className={classes.label}>
          {props.label}
        </Typography>
      ) : (
        ''
      )}
    </div>
  );
};
// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default Checkbox;
